import { useCallback, useMemo, useState } from "react";
import moment from "moment-timezone";
import type { DateRange } from "react-day-picker";
import { CalendarIcon, XIcon } from "lucide-react";
import {
    Popover,
    PopoverAnchor,
    PopoverContent,
} from "../../../ui/popover";
import { Button } from "../../../ui/button";
import { Calendar } from "../../../ui/calendar";
import Single from "./anchor/Single";
import Multiple from "./anchor/Multiple";
import Range from "./anchor/Range";
import { useCompanyDateFormat, useCompanyWeekStartsOn } from "../../../../utils/hooks/company";
import { cn } from "../../../../lib/utils";
import type { DateInputProps } from "./types";

function DateInput({
    disabled,
    value,
    mode = "single",
    className,
    placeholder,
    minDate,
    maxDate,
    onChange,
}: DateInputProps) {
    const [open, setOpen] = useState(false);
    const weekStartsOn = useCompanyWeekStartsOn() as 0 | 1 | 2 | 3 | 4 | 5 | 6;
    const dateFormat = useCompanyDateFormat() || "YYYY-MM-DD";

    const [draft, setDraft] = useState<string | null>(null);
    const [draftFrom, setDraftFrom] = useState<string | null>(null);
    const [draftTo, setDraftTo] = useState<string | null>(null);

    const singleSelected = value instanceof Date ? value : undefined;

    const multipleSelected = useMemo(() => {
        return Array.isArray(value)
            ? [...value].sort((a, b) => a.getTime() - b.getTime())
            : undefined;
    }, [value]);

    const rangeSelected = useMemo(() => {
        return value &&
            typeof value === "object" &&
            !Array.isArray(value) &&
            !(value instanceof Date)
            ? (value as DateRange)
            : undefined;
    }, [value]);

    const format = useCallback((date?: Date) => date ? moment(date).format(dateFormat) : "", [dateFormat]);

    const calendarDisabled = useMemo(() => {
        if (!minDate && !maxDate) {
            return disabled;
        }

        const matchers: any[] = [!!disabled];
        if (minDate) {
            matchers.push({ before: minDate });
        }
        if (maxDate) {
            matchers.push({ after: maxDate });
        }
        return matchers;
    }, [disabled, minDate, maxDate]);

    const clear = useCallback(() => {
        setDraft(null);
        setDraftFrom(null);
        setDraftTo(null);
        onChange?.(null);
    }, [onChange]);

    const anchor = useMemo(() => {
        return {
            single: (
                <Single
                    draft={draft}
                    setDraft={setDraft}
                    setOpen={setOpen}
                    placeholder={placeholder}
                    formattedValue={format(singleSelected)}
                    disabled={disabled}
                    dateFormat={dateFormat}
                    onChange={onChange}
                />
            ),
            multiple: (
                <Multiple
                    multipleSelected={multipleSelected}
                    setOpen={setOpen}
                    placeholder={placeholder}
                    disabled={disabled}
                    dateFormat={dateFormat}
                    onChange={onChange}
                />
            ),
            range: (
                <Range
                    rangeSelected={rangeSelected}
                    setOpen={setOpen}
                    draftFrom={draftFrom}
                    setDraftFrom={setDraftFrom}
                    draftTo={draftTo}
                    setDraftTo={setDraftTo}
                    disabled={disabled}
                    dateFormat={dateFormat}
                    onChange={onChange}
                />
            ),
        };
    }, [
        dateFormat,
        disabled,
        draft,
        draftFrom,
        draftTo,
        multipleSelected,
        onChange,
        placeholder,
        rangeSelected,
        singleSelected,
        format
    ]);

    const calendarProps = useMemo(() => {
        return {
            single: {
                mode: "single" as const,
                selected: singleSelected,
                onSelect: (date?: Date) => {
                    setDraft(null);
                    onChange?.(date);
                    setOpen(false);
                },
            },
            multiple: {
                mode: "multiple" as const,
                selected: multipleSelected,
                onSelect: (dates?: Date[]) => onChange?.(dates),
            },
            range: {
                mode: "range" as const,
                selected: rangeSelected,
                numberOfMonths: 2,
                onSelect: (range?: DateRange) => onChange?.(range),
            },
        };
    }, [multipleSelected, onChange, rangeSelected, singleSelected]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverAnchor asChild>
                <div className={cn("tw-relative tw-flex tw-items-center", className)}>
                    {anchor[mode]}

                    <div className="tw-absolute tw-right-1.5 tw-flex tw-items-center tw-gap-0.5">
                        {value && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                disabled={disabled}
                                className="tw-size-5 tw-text-muted-foreground hover:tw-text-foreground"
                                onClick={clear}
                            >
                                <XIcon className="tw-size-3.5" />
                            </Button>
                        )}

                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            disabled={disabled}
                            className="tw-size-5 tw-text-muted-foreground hover:tw-text-foreground"
                            onClick={() => setOpen((o) => !o)}
                        >
                            <CalendarIcon className="tw-size-3.5" />
                        </Button>
                    </div>
                </div>
            </PopoverAnchor>

            <PopoverContent className="tw-w-auto tw-p-3 tw-rounded-xl tw-border-border/60 tw-shadow-lg" align="start">
                <Calendar
                    disabled={calendarDisabled}
                    weekStartsOn={weekStartsOn}
                    className="tw-p-0"
                    classNames={{
                        today: "tw-rounded-lg tw-bg-primary/5 tw-font-semibold",
                        day: "tw-cursor-pointer hover:tw-bg-primary/5 tw-rounded-lg hover:tw-text-primary data-[disabled]:tw-text-muted-foreground data-[disabled]:hover:tw-bg-transparent data-[selected]:tw-bg-primary/5 data-[selected]:tw-text-primary-foreground",
                    }}
                    captionLayout="dropdown"
                    {...calendarProps[mode]}
                />
            </PopoverContent>
        </Popover>
    );
}

export default DateInput;
